import React, { useCallback, useState } from 'react';
import { message } from 'antd';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DataTable from '@/components/DataTable';
import Filter, { FilterConfig } from '@/components/Filter';
import { getTransactionColumns } from './columns';
import { getTransactionList, ITransaction } from '@/api/commitment';
import { isSupportedChainId, SupportedChains } from '@packet/constants/AppSupportChains';
import { TransactionType, TransactionTypeName } from '@/types/commitment';
import { serializeTransactionHash } from '@/utils/transaction';
import { useFilterAndPagination } from '@/hooks/useFilterAndPagination';
import { useChainStore } from '@/stores/chain';
import DownOutlined from '@/assets/icon/down-outlined.svg?react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ChainConfigMap } from '@/const/chains';
import { timeFmt } from '@/utils/timeFmt';

// 页面标题
const PAGE_TITLE = 'Transaction History';

// 筛选配置
const FILTER_CONFIGS: FilterConfig[] = [
  {
    key: 'chain_id',
    placeholder: 'Network',
    options: SupportedChains.map((chain) => ({
      value: chain.id,
      label: chain.name,
    })),
    defaultValue: SupportedChains[0].id, // 默认选择第一个网络
    defaultValueFn: () => {
      const chainId = useChainStore.getState().chainId;
      return chainId || SupportedChains[0].id;
    },
    allowClear: false, // 网络筛选器不允许清除，必须有选择
    minWidth: 220,
    changeClear: ['asset'],
  },
  {
    key: 'type',
    placeholder: 'Type',
    options: Object.entries(TransactionTypeName)
      .map(([key, value]) => ({
        value: parseInt(key), // 确保value是数字类型，与URL参数类型一致
        label: value,
      }))
      .filter((o) => o.label !== 'Swap'), // 过滤掉 Swap 类型
    allowClear: true, // 交易类型可以清除
    minWidth: 100,
  },
  {
    key: 'asset',
    placeholder: 'Token',
    options: [],
    allowClear: true, // token 可以清除
  },
];

const formatFeeForCsv = (tx: ITransaction): string => {
  const protocolFees = tx.protocol_fee?.map((fee) => BigInt(fee)) ?? [];
  const relayerFee = tx.relayer_fee ? BigInt(tx.relayer_fee) : BigInt(0);

  if (tx.tx_type === TransactionType.Swap && protocolFees.length >= 2) {
    const formatLine = (amount: bigint) => {
      return `${amount.toString()}`;
    };

    return [formatLine(protocolFees[0] + relayerFee), formatLine(protocolFees[1])].join('\n');
  }

  const totalFee = protocolFees.reduce((acc, curr) => acc + curr, BigInt(0)) + relayerFee;
  return totalFee.toString();
};

export default function TransactionHistory() {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  // 数据加载函数
  const loadTransactions = useCallback(async (filterParams: any, paginationParams: { current: number; pageSize: number }) => {
    setLoading(true);
    try {
      // 获取网络配置
      let selectedChainId = Number(filterParams?.chain_id ? filterParams.chain_id : SupportedChains[0].id);
      if (!isSupportedChainId(selectedChainId)) selectedChainId = SupportedChains[0].id;
      const selectedChain = ChainConfigMap[selectedChainId as keyof typeof ChainConfigMap];
      console.log('selectedChain', selectedChain, ChainConfigMap, selectedChainId);

      // 构建API参数
      const apiParams: any = {
        offset: (paginationParams.current - 1) * paginationParams.pageSize,
        limit: paginationParams.pageSize,
        address: selectedChain.appContract,
        chain_id: selectedChainId,
        order: 'desc',
      };

      // 添加交易类型筛选
      if (filterParams?.type) {
        apiParams.tx_type = parseInt(filterParams.type);
      }

      // 添加时间范围筛选
      if (filterParams?.dateRange && Array.isArray(filterParams.dateRange) && filterParams.dateRange.length === 2) {
        const startDateStr = filterParams.dateRange[0];
        const endDateStr = filterParams.dateRange[1];
        if (startDateStr && endDateStr) {
          // 将日期字符串转换为时间戳（秒）
          apiParams.begin_time = Math.floor(new Date(startDateStr).getTime() / 1000);
          apiParams.end_time = Math.floor(new Date(endDateStr + ' 23:59:59').getTime() / 1000);
        }
      }

      if (filterParams?.asset) {
        apiParams.asset = filterParams.asset;
      }

      const res = await getTransactionList(apiParams);
      if (res.code !== 0) {
        message.error(res.message);
        return;
      }
      setTransactions(res?.data?.data || []);
      setPaginationTotal(res?.data?.total || 0);
    } catch (error) {
      message.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  // 使用通用的筛选和分页hook
  const { filters, pagination, loading, handleFilterChange, dynamicFilterConfigs, setPaginationTotal, setLoading } = useFilterAndPagination({
    filterConfigs: FILTER_CONFIGS,
    defaultPageSize: 10,
    onDataLoad: loadTransactions,
    autoLoad: true,
  });

  // 交易数据状态
  const [transactions, setTransactions] = useState<ITransaction[]>([]);

  // 处理行点击（查看详情）
  const handleRowClick = (record: ITransaction) => {
    const selectedChainId = filters.chain_id || SupportedChains[0].id;
    const tx = serializeTransactionHash(record.txhash, record.tx_index);
    navigate(`/transaction/${selectedChainId}/${tx}`);
  };

  return (
    <PageContainer>
      <MainContent>
        {/* 页面标题和筛选器区域 */}
        <HeaderSection>
          <PageTitle>
            {PAGE_TITLE}

            {isMobile && (
              <MobileFilterButton onClick={() => setShowMobileFilter((prev) => !prev)}>
                Filter
                <ArrowIcon $open={showMobileFilter}>
                  <DownOutlined />
                </ArrowIcon>
              </MobileFilterButton>
            )}
          </PageTitle>

          {/* PC端始终显示，移动端根据 showMobileFilter 控制 */}
          {(!isMobile || showMobileFilter) && (
            <FilterSection>
              <Filter
                onFilterChange={handleFilterChange}
                initialFilters={filters}
                loading={loading}
                filterConfigs={dynamicFilterConfigs}
                showDateRange={true}
                showDownload={true}
                downloadProps={{
                  filename: `transaction-history-${new Date().toISOString().split('T')[0]}`,
                  onGetData: () => {
                    // 格式化交易数据用于CSV导出
                    return transactions.map((tx) => ({
                      'Transaction Hash': tx.txhash,
                      'Transaction Index': tx.tx_index,
                      Type: TransactionTypeName[tx.tx_type],
                      Fee: formatFeeForCsv(tx),
                      'Created At': timeFmt(tx.created_at),
                      Outputs: tx.outputs ? tx.outputs.map((output) => output.user_pk).join(',') : '',
                      Inputs: tx.inputs ? tx.inputs.map((input) => input.user_pk).join(',') : '',
                    }));
                  },
                  onBeforeDownload: () => {
                    console.log('Starting transaction history download...');
                  },
                  onAfterDownload: () => {
                    message.success('Transaction history downloaded successfully!');
                  },
                }}
              />
            </FilterSection>
          )}
        </HeaderSection>

        {/* 表格区域 */}
        <TableSection>
          <DataTable
            data={transactions}
            columns={getTransactionColumns(filters.chain_id || SupportedChains[0].id, handleRowClick)}
            loading={loading}
            pagination={pagination}
            onRowClick={handleRowClick}
            rowKey={(record: ITransaction) => `${record.txhash}-${record.tx_index}`}
            statsInfo={{
              label: 'More than',
              value: pagination.total,
            }}
          />
        </TableSection>
      </MainContent>
    </PageContainer>
  );
}

// Styled Components
const PageContainer = styled.div`
  background: #ffffff;
`;

const MainContent = styled.div`
  max-width: var(--pc-max-width);
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 0 16px;
    overflow-x: hidden;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 40px 0 16px 0;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #141413;
  margin: 0;
  line-height: 1;
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media (max-width: 1024px) {
    flex-wrap: wrap;
    width: 100%;
  }
`;

const TableSection = styled.div`
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
`;

const MobileFilterButton = styled.button`
  display: inline-flex;
  height: 40px;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid rgba(16, 41, 0, 0.2);
  background: #fff;
`;

const ArrowIcon = styled.span<{ $open: boolean }>`
  display: inline-block;
  margin-left: 4px;
  font-size: 16px;
  transition: transform 0.2s;
  transform-origin: center;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`;
